import { reactive } from 'vue';
/**
 * using this to override console log for debug bool
 */
const debug = {
    show: false,
    log(...args) {
        if (this.show)
            console.log(...args);
    }
};
/**
 * SocketClient
 * creates a new socket client instance, created by plugin
 */
export class SocketClient extends EventTarget {
    /**
     * @constructor
     *
     * @param options -  Options used to defined the web socket connection.
     */
    constructor(options) {
        super();
        this.options = options;
        debug.show = options.debug || false;
        this.ws = null;
        this.connection = {
            promise: null,
            resolve: null,
            reject: null
        };
        this.state = reactive({
            connected: false
        });
        this.init();
    }
    /**
     * Indicates if the socket is currently connected to the server.
     */
    get connected() {
        const { state } = this;
        return state.connected;
    }
    /**
     * Register the provided method to be called once the socket is connected to the server.
     * If the socket is already connected, the method will be called immediately.
     *
     * @param func - The method to call once the socket is connected.
     */
    whenConnected(func) {
        const { connection } = this;
        if (!!connection.promise) {
            connection.promise.then(() => func());
        }
    }
    /**
     * Attempts to connect to the server.
     *
     * @returns - A promise that resolves once the socket is connected.
     */
    connect() {
        const { state, connection, options } = this;
        if (state.connected) {
            this.close();
        }
        if (!connection.promise) {
            throw new Error('Promise is undefined.');
        }
        debug.log(`[SocketClient] connecting to: ${options.host}`);
        const socket = new WebSocket(options.host);
        socket.addEventListener('open', () => {
            console.log('[SocketClient] connected');
            state.connected = true;
            if (!!connection.resolve) {
                connection.resolve();
            }
        });
        socket.addEventListener('error', (ev) => {
            console.warn('[SocketClient] error, closing...');
            // If we won't automatically reconnect, reject the connection promise.
            if (!options.reconnect && connection.reject) {
                connection.reject(ev);
            }
            this.close();
        });
        socket.addEventListener('close', () => {
            debug.log('[SocketClient] closed');
            if (options.reconnect) {
                debug.log('[SocketClient] reconnecting in 3s...');
                setTimeout(() => this.connect(), 3000);
            }
        });
        socket.addEventListener('message', event => this.parseMessage(event.data));
        this.ws = socket;
        return connection.promise;
    }
    /**
     * Sends the provided message
     *
     * @param obj - The message to send.
     */
    sendObject(obj) {
        debug.log('[SocketClient] sendObject:', obj);
        try {
            const message = JSON.stringify(obj);
            if (this.ws) {
                this.ws.send(message);
            }
            else {
                console.warn('[SocketClient] sendObject, socket not connected');
            }
        }
        catch (err) {
            console.warn('[SocketClient] error sending object as message:', err);
        }
    }
    /**
     * Closes the connection to the server.
     */
    close() {
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        this.init();
    }
    init() {
        const { state, connection } = this;
        state.connected = false;
        connection.promise = new Promise((resolve, reject) => {
            connection.resolve = resolve;
            connection.reject = reject;
        });
    }
    parseMessage(message) {
        debug.log('[SocketClient] parseMessage:', message);
        try {
            const props = JSON.parse(message);
            const event = props.event;
            delete props.event;
            this.dispatch(event, props);
        }
        catch (err) {
            console.warn('[SocketClient] error parsing json from message:', err);
        }
    }
    dispatch(event, props) {
        // signals anything listening directly on this: $socket.addEventListener()
        this.dispatchEvent(new CustomEvent(event, { detail: props }));
        // SocketListener vue components internally just listen to an abstract message event
        this.dispatchEvent(new CustomEvent('message', { detail: { event, props } }));
    }
}
